import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { iconsList } from '../../../utils/icons'
import { ButtonIconChooser } from './ButtonIconChooser'
import { SearchBar } from '../SearchBar'
import i18n from 'live/services/i18n'

type iconChooserProps = {
    choosedIcon?: string
    setIsIconChoserOpen: (boolean) => void
    saveIcon: (icon: string) => void
    onSave: () => void
}

export const IconChooser = ({ choosedIcon, setIsIconChoserOpen, saveIcon, onSave }: iconChooserProps): JSX.Element => {
    const [iconChoosed, setIconChoosed] = useState(choosedIcon)
    const [searched, setSearched] = useState(null)

    const handleSelectIcon = value => {
        setIconChoosed(value)
        saveIcon(value)
    }

    const filteredIcons = useMemo(() => {
        const filtered = searched
            ? iconsList?.filter(stage => stage?.toLowerCase()?.includes(searched?.toLowerCase()))
            : iconsList
        return filtered
    }, [searched, iconsList])

    return (
        <Container>
            <InnerContainer>
                <LabelHeader>
                    <span>{i18n('Label Icon')}</span>
                    <button
                        onClick={() => {
                            setIsIconChoserOpen(false)
                            onSave()
                        }}
                    >
                        <i className="far fa-times" />
                    </button>
                </LabelHeader>

                <SearchBar
                    placeholder={i18n('Type to search')}
                    value={searched}
                    onChange={e => setSearched(e || null)}
                />

                <IconsContainer>
                    {filteredIcons.map(icon => {
                        const isIconSelected = icon === iconChoosed
                        return (
                            <ButtonIconChooser
                                key={icon}
                                buttonContent={`far fa-${icon}`}
                                isIconSelected={isIconSelected}
                                onClick={() => handleSelectIcon(icon)}
                                value={icon}
                            />
                        )
                    })}
                </IconsContainer>
            </InnerContainer>
        </Container>
    )
}

const Container = styled.div`
    position: absolute;
    background: rgba(54, 65, 89, 0.25);
    display: flex;
    justify-content: flex-end;
    width: 100%;
    height: 556px;
    top: 49px;
    right: 0px;
    z-index: 10;
`

const InnerContainer = styled.div`
    padding: 16px;
    width: 378px;
    height: 100%;
    background: ${({ theme }) => theme?.['gray-100'] || '#F5F5F5'};
`

const IconsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    overflow: scroll;
    overflow-x: hidden;
    height: calc(100% - 55px);
    align-content: flex-start;

    &::-webkit-scrollbar {
        width: 4px;
        height: 4px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
        border-radius: 1rem;
    }

    &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme?.['gray-700'] || '#606A80'};
        display: none;
        border-radius: 1rem;
        background-clip: padding-box;
    }

    &:hover {
        &::-webkit-scrollbar-thumb {
            display: flex;
        }
    }

    button {
        width: 64px;
        height: 46px;
        margin: 2px;
        padding: 4px;
        gap: 4px;
        display: flex;
        color: ${({ theme }) => theme?.['gray-600'] || '#364159'};
        flex-direction: column;
        align-content: center;
        justify-content: center;
        align-items: center;
    }
`

const LabelHeader = styled.div`
    width: 100%;
    height: 24px;
    margin-bottom: 8px;

    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 24px;
    color: ${({ theme }) => theme?.['gray-800'] || '#364159'};
    display: flex;
    justify-content: space-between;

    button {
        width: 8px;
        height: 16px;
        color: ${({ theme }) => theme?.['gray-600'] || '#364159'};
        font-style: normal;
        font-weight: 400;
        font-size: 14px;
        line-height: 16px;
        background-color: ${({ theme }) => theme?.['gray-100'] || '#F5F5F5'};

        border: none;
    }
`
